/**
 * What we tell somebody when authentication fails, and what we refuse to tell
 * them.
 *
 * The difference between "no account with that address" and "wrong password" is
 * how a stranger finds out who works at a company. Every sign-in failure gets
 * the same sentence, and so does every request for a password-reset link,
 * whether or not the address is registered. The single-tenant implementation
 * learned this the same way; the rule is inherited deliberately.
 *
 * Nothing here ever shows the provider's own message. Those leak — "User
 * already registered" is an existence oracle in four words.
 */

export interface AuthFailure {
    message?: string;
    code?: string;
    status?: number;
}

/** The one thing a failed sign-in is ever allowed to say. */
export const SIGN_IN_FAILED = 'That email and password do not match. Check both and try again.';

/** The one thing a sign-up or a reset request is ever allowed to say. */
export const CHECK_YOUR_EMAIL =
    'If that address can be used here, we have sent it a link. It is good for one hour; if more than one email arrives, use the newest.';

const RATE_LIMITED = 'Too many attempts. Wait a few minutes and try again.';
const NETWORK = 'We could not reach the server. Check your connection and try again.';
const GENERIC = 'Something went wrong. Try again, and tell us if it keeps happening.';

const text = (e: AuthFailure | null | undefined): string =>
    `${e?.code ?? ''} ${e?.message ?? ''}`.toLowerCase();

const isRateLimit = (e: AuthFailure | null | undefined): boolean =>
    e?.status === 429 || /rate limit|too many|over_email_send_rate/.test(text(e));

const isNetwork = (e: AuthFailure | null | undefined): boolean =>
    /failed to fetch|network|econnrefused|fetch failed/.test(text(e));

/**
 * A sign-in failure, in words that do not say which half was wrong.
 *
 * Rate limiting and an unreachable server are worth distinguishing: they are
 * not about the account, and hiding them just makes people retype a correct
 * password.
 */
export function signInError(e: AuthFailure | null | undefined): string {
    if (isNetwork(e)) return NETWORK;
    if (isRateLimit(e)) return RATE_LIMITED;
    if (/email not confirmed|not_confirmed/.test(text(e))) {
        return 'Confirm your email address first. Check your inbox for the link we sent.';
    }
    return SIGN_IN_FAILED;
}

/**
 * A sign-up failure.
 *
 * Everything that is merely "this address is taken" becomes the same sentence a
 * successful sign-up gets, so the form cannot be used to test whether an
 * address is registered. Only problems with what was typed are reported
 * honestly, because the person can act on those.
 */
export function signUpError(e: AuthFailure | null | undefined): string | null {
    if (!e) return null;
    if (isNetwork(e)) return NETWORK;
    if (isRateLimit(e)) return RATE_LIMITED;
    if (/already registered|already exists|user_already_exists/.test(text(e))) return null;
    if (/password/.test(text(e))) return 'That password does not meet the rules below.';
    if (/email|invalid/.test(text(e))) return 'That does not look like an email address.';
    return GENERIC;
}

/** A request for a reset link never reports anything about the address. */
export function resetRequestError(e: AuthFailure | null | undefined): string | null {
    if (!e) return null;
    if (isNetwork(e)) return NETWORK;
    if (isRateLimit(e)) return RATE_LIMITED;
    return null;
}

/** Setting a new password, from a link. Here the problems are worth naming. */
export function newPasswordError(e: AuthFailure | null | undefined): string {
    if (isNetwork(e)) return NETWORK;
    if (isRateLimit(e)) return RATE_LIMITED;
    // Auth sets a password without the current one only for a session the reset link opened
    // (Phase 0 hardening, update_password_require_current_password). A session that merely came
    // here signed in is refused by Auth itself; say what to do instead.
    if (/^current_password/.test(e?.code ?? '') || /current password/.test(text(e))) {
        return 'Open the newest link in your reset email to choose a new password here, or change it from Your account with your current password.';
    }
    if (/expired|invalid|not found|token/.test(text(e))) {
        return 'That link has expired or has already been used. Ask for a new one.';
    }
    if (/same.*password|different from the old/.test(text(e))) {
        return 'Choose a password you have not used here before.';
    }
    if (/password/.test(text(e))) return 'That password does not meet the rules below.';
    return GENERIC;
}

/* --------------------------------- passwords -------------------------------- */

/**
 * The rules, stated before somebody types rather than after they fail.
 *
 * These mirror `supabase/config.toml` (`minimum_password_length = 10`,
 * `password_requirements = "lower_upper_letters_digits"`). The server enforces
 * them; this is so the form can say what it wants up front, and the two must
 * not drift.
 */
export const PASSWORD_MIN_LENGTH = 10;

export const PASSWORD_RULES: readonly string[] = [
    `At least ${PASSWORD_MIN_LENGTH} characters`,
    'An upper-case and a lower-case letter',
    'At least one digit',
];

/** Which rules a candidate password fails, in the order they are listed. */
export function passwordProblems(password: string): string[] {
    const pw = password ?? '';
    const problems: string[] = [];
    if (pw.length < PASSWORD_MIN_LENGTH) problems.push(PASSWORD_RULES[0]!);
    if (!/[a-z]/.test(pw) || !/[A-Z]/.test(pw)) problems.push(PASSWORD_RULES[1]!);
    if (!/[0-9]/.test(pw)) problems.push(PASSWORD_RULES[2]!);
    return problems;
}

export const passwordIsAcceptable = (password: string): boolean =>
    passwordProblems(password).length === 0;

/* ---------------------------------- email ---------------------------------- */

/**
 * Shaped like an address, and nothing stricter.
 *
 * Deliberately no blocklist of Gmail or Outlook. Plenty of real businesses run
 * on them, and refusing those addresses refuses customers; a verified address is
 * the requirement (04 section 2).
 */
export function isEmailShaped(value: string): boolean {
    const v = (value ?? '').trim();
    if (v.length < 6 || v.length > 254) return false;
    if (/\s/.test(v)) return false;
    return /^[^@]+@[^@.]+(\.[^@.]+)+$/.test(v);
}

/** Addresses are compared and stored lower-cased, everywhere. */
export const normaliseEmail = (value: string): string => (value ?? '').trim().toLowerCase();
